import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useBoolean,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import * as React from 'react';

import { Pagination } from '@/components/Pagination';
import { TableEmpty } from '@/components/Table';
import { EInvoiceStatus, OtherItemCategoryStatus } from '@/graphql-types/globalTypes';
import { GetInvoicesFiltersInput } from '@/graphql-types/globalTypes';
import { Card } from '@/imports/ui/chakra/components/Card';
import { SearchIcon } from '@/imports/ui/chakra/feather';
import { useDebouncedValue } from '@/imports/ui/hooks/useDebouncedValue';

// import { MenuSelect, MenuSelectOption } from '../Common/MenuSelect';
// import { SkeletonTableBody } from '../Common/SkeletonTableBody';
import _, { set } from 'lodash';
import { MenuSelect } from '../../OtherItems/components/Common/MenuSelect';
import { OtherItemsCategoryListActions } from './OtherItemsCategoryListActions';
import { useQuery } from '@apollo/client';
import { GET_CATEGORY } from '../graphql';
import { GetCategories } from '../graphql-types/GetCategories';
import { SkeletonTableBody } from '../../OtherItems/components/Common/SkeletonTableBody';
import { useCreateCategory } from '../hooks/useCreateCategory';
import { useUpdateCategory } from '../hooks/useUpdateCategory';
import { useChangeCategoryStatus } from '../hooks/useChangeCategoryStatus';

const pageSizeOptions = [10, 25, 50];

const statusOptions: any[] = [
  { label: 'All', value: '' },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Inactive', value: 'INACTIVE' },
];


const showToast = (
  toast: ReturnType<typeof useToast>,
  title: string,
  description: string,
  status: 'success' | 'error',
  duration = 2000
) => {
  toast({
    title,
    description,
    status,
    duration,
    isClosable: true,
    // position: 'top-right'
  });
};

export const OtherItemsCategoryList: React.FC<{}> = ({ }) => {
  const toast = useToast();
  const [page, setPage] = React.useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(10);
  const [searchInput, setSearchInput] = React.useState('');
  const [search, setSearch] = React.useState('');
  const debouncedSearchValue = useDebouncedValue(searchInput, 500);
  const [filter, setFilter] = React.useState<{ status?: string }>({
    status: '',
  });
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  // status toggle state
  // const [isStatusChecked, setIsStatusChecked] = React.useState(false);
  // this state will hold the id of the row being edited
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editValue, setEditValue] = React.useState("");
  // state for adding new category
  const [isAddingNew, setIsAddingNew] = React.useState(false);
  const [newCategoryName, setNewCategoryName] = React.useState("");
  // common loader state for save action
  const [isSaving, setIsSaving] = useBoolean(false);

  const { createCategory } = useCreateCategory();
  const { updateCategory } = useUpdateCategory();
  const { changeCategoryStatus } = useChangeCategoryStatus();


  // handle filter change
  const handleFilterChange = (newFilter: any) => {
    const updatedFilter = { ...filter, isDraft: false, ...newFilter };
    if (!updatedFilter.isDraft) {
      delete updatedFilter.isDraft;
    }
    setFilter(updatedFilter);
    setPage(1);
  };


  // When user changes debounced search value, update search state
  React.useEffect(() => {
    setSearch(debouncedSearchValue);
    setPage(1);
  }, [debouncedSearchValue]);

  React.useEffect(() => {
      setPage(1);
    }, [itemsPerPage]);

  // this is for the query variables
  const listQueryVariables = React.useMemo(() => {
    const cleanedFilter = {
      ...filter,
    };
    // remove empty status
    if (!cleanedFilter.status) {
      delete cleanedFilter.status;
    }
    return {
      input: {
        limit: itemsPerPage,
        offset: (page - 1) * itemsPerPage,
        search,
        filter:
          Object.keys(cleanedFilter).length > 0
            ? cleanedFilter
            : {},
      },
    };
  }, [itemsPerPage, page, search, filter]);

  // here is the query to get the data
  const { data, loading, error } = useQuery<
    GetCategories
  >(GET_CATEGORY, {
    variables: listQueryVariables,
    fetchPolicy: 'cache-and-network',
  });

  // List Data Variables
  const rows = data?.getCategories.results || [];

  //Pagination counts and calculations
  const currentTotalCount = data?.getCategories.totalCount ?? 0;
  const totalPages = Math.ceil(currentTotalCount / itemsPerPage);

  // handle edit action
  const onEdit = (id: string) => {
    setIsAddingNew(false);
    const item = rows.find((r) => r._id === id);
    if (!item) return;
    setEditingId(id);
    setEditValue(item.name);
  };

  // handle view action
  const onAddNew = () => {
    if (isAddingNew) {
      showToast(
        toast,
        'Error',
        'You cannot create another category. Please complete the current form first.',
        'error'
      );
      return;
    }

    setIsAddingNew(true);
    setEditingId('new');
    setEditValue('');
    setNewCategoryName('');
  };

  // handle save action
  const onSave = async () => {
    try {
      setIsSaving.on();
      const name =
        editingId === 'new' ? newCategoryName : editValue;

      if (!name.trim()) {
        showToast(toast, 'Error', 'Category name is required', 'error');
        setIsSaving.off();
        return;
      }

      if (editingId === 'new') {
        const res = await createCategory({
          name: newCategoryName,
        });

        showToast(toast, 'Success', res.message, 'success');
      } else {
        const res = await updateCategory({
          _id: editingId!,
          name: editValue,
        });

        showToast(toast, 'Success', res.message, 'success');
      }
      setIsSaving.off();
      setIsAddingNew(false);
      setEditingId(null);
      setNewCategoryName('');
      setEditValue('');
    } catch (err: any) {
      setIsSaving.off();
      showToast(toast, 'Error', err.message, 'error');
    }finally {
      setIsSaving.off();
    }
  };
  // handle cancel action
  const onCancel = () => {
    setIsAddingNew(false);
    setEditingId(null);
    setNewCategoryName('');
    setEditValue('');
  };

  // if error show toast while fetching data
  React.useEffect(() => {
    if (error) {
      showToast(
        toast,
        'Error',
        error.message || 'Failed to load other items category',
        'error',
        5000
      );
    }
  }, [error, toast]);

  return (
    <VStack spacing={6} align="stretch">
      <Card bg="white" p={6}>
        {/* Search + action */}
        <Flex justify="space-between" mb={6} align="center">
          <Text fontSize="xl" fontWeight="semibold">
            Other Items Category
          </Text>
        </Flex>
        {/* Search Input */}
        <Flex
          justify="space-between"
          align={{ base: 'left', lg: 'center' }}
          gap={4}
          flexDirection={{ base: 'column', md: 'column', lg: 'row' }}
          flexWrap="wrap"
          mb={6}
        >
          <Flex
            align={{ base: 'left', lg: 'center' }}
            justify={{ base: 'left' }}
            flexWrap="wrap"
            flexDirection={{ base: 'column', md: 'column', lg: 'row' }}
            gap={4}
          >
            <Box>
              <InputGroup w="320px">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder={`Search by Name`}
                  value={searchInput}
                  onChange={(e) => {setSearchInput(e.target.value);setIsAddingNew(false);}}
                  bg="white"
                  borderColor="gray.300"
                  _focus={{
                    borderColor: 'blue.500',
                    boxShadow: '0 0 0 1px #3182ce',
                  }}
                />
              </InputGroup>
            </Box>

            {/* Status Filter */}
            <Box w="150px">
              {/* status dropdown , default is all */}
              <MenuSelect
                value={
                  filter.status ? filter.status : ''
                }
                placeholder="Status"
                onChange={(value: any) => {
                  setIsAddingNew(false);
                  if (value === '') {
                    handleFilterChange({ status: null });
                  } else if (value === 'draft') {
                    handleFilterChange({ isDraft: true, status: null });
                  } else {
                    handleFilterChange({ status: value as EInvoiceStatus });
                  }
                }}
                options={statusOptions}
                maxHeight={'100%'}
              />
            </Box>
          </Flex>
          <Button onClick={() => onAddNew()} w="max-content">
            Create New Category
          </Button>
        </Flex>

        {/* Table */}
        <Box overflow="hidden" pb={6}>
          <Box overflowX="auto" minH="40vh" borderRadius="md">
            <Table size="sm">
              <Thead>
                <Tr
                  borderBottom="1px"
                  // color={textColor}
                  borderColor={borderColor}
                >
                  <Th py={3}>Name</Th>
                  <Th py={3}>Status</Th>
                  <Th py={3}>Actions</Th>
                </Tr>
              </Thead>
              {
                loading ? (
                  <SkeletonTableBody
                    columnCount={3}
                    useFullWidth={true}
                    rows={10}
                  />
                ) : error ? (
                  <Tbody>
                    <Tr>
                      <Td colSpan={3}>
                        <Text color="red.500">Failed to load other items</Text>
                      </Td>
                    </Tr>
                  </Tbody>
                ) :
                  <>
                    <Tbody>
                      {/* Existing rows */}
                      {rows.map((row: any) => (
                        <Tr key={row._id} _hover={{ bg: hoverBg }}>
                          <Td>
                            {editingId === row._id ? (
                              <Flex gap={2}>
                                <Input
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  maxW="200px"
                                  size="sm"
                                />
                              </Flex>
                            ) : (
                              <Tooltip label={row.name ?? 'Category Name'}>
                                <Text
                                  width="200px"
                                  overflow="hidden"
                                  textOverflow="ellipsis"
                                  whiteSpace="nowrap"
                                >
                                  {row.name}
                                </Text>
                              </Tooltip>
                            )}
                          </Td>

                          <Td>
                            <Switch
                              colorScheme="primary"
                              isChecked={row.status === OtherItemCategoryStatus.ACTIVE}
                              size="lg"
                              onChange={async () => {
                                const nextStatus =
                                  row.status === OtherItemCategoryStatus.ACTIVE
                                    ? OtherItemCategoryStatus.INACTIVE
                                    : OtherItemCategoryStatus.ACTIVE;

                                try {
                                  await changeCategoryStatus(row._id, {
                                    optimisticResponse: {
                                      changeCategoryStatus: {
                                        __typename: 'ChangeCategoryStatusOutput',
                                        success: true,
                                        message: 'Status updated',
                                      },
                                    },
                                    update(cache) {
                                      const cacheId = cache.identify({
                                        __typename: 'OtherItemCategory',
                                        _id: row._id,
                                      });

                                      cache.modify({
                                        id: cacheId,
                                        fields: {
                                          status() {
                                            return nextStatus;
                                          },
                                        },
                                      });
                                    },
                                  });

                                  showToast(
                                    toast,
                                    'Success',
                                    'Category status updated successfully',
                                    'success'
                                  );
                                } catch (err) {
                                  console.error(err);
                                }
                              }}

                            />
                          </Td>

                          <Td py={2}>
                            <OtherItemsCategoryListActions
                              row={row}
                              onEdit={onEdit}
                              onAddNew={onAddNew}
                              onSave={onSave}
                              onCancel={onCancel}
                              editingId={editingId}
                              isSaving={isSaving}
                            />
                          </Td>
                        </Tr>
                      ))}

                      {/* New category row */}
                      {isAddingNew && (
                        <Tr key="new" _hover={{ bg: hoverBg }}>
                          <Td>
                            <Flex gap={2}>
                              <Input
                                value={newCategoryName}
                                onChange={(e) => setNewCategoryName(e.target.value)}
                                maxW="200px"
                                size="sm"
                                placeholder='Enter category name'
                              />
                            </Flex>
                          </Td>

                          <Td>
                            <Switch isDisabled isChecked={true} size="lg" />
                          </Td>

                          <Td py={2}>
                            <OtherItemsCategoryListActions
                              row={{ _id: 'new', name: newCategoryName }}
                              onEdit={onEdit}
                              onAddNew={onAddNew}
                              onSave={onSave}
                              onCancel={onCancel}
                              editingId={editingId}
                              isSaving={isSaving}
                            />
                          </Td>
                        </Tr>
                      )}


                    </Tbody>
                    {/* Empty state */}
                    {!isAddingNew && rows.length === 0 && (
                      <TableEmpty>
                        There are no other items category found
                      </TableEmpty>
                    )}
                  </>
              }
            </Table>
          </Box>

          {/* Pagination */}
          <Flex justify={Math.ceil(currentTotalCount / itemsPerPage) > 1 ? "space-between" : "center"} align="center" gap={4} flexWrap="wrap">
            {currentTotalCount > 10 ? (
              <Box display="flex" alignItems="center" mt={6}>
                <Box as="span" fontSize="sm" mr={2}>
                  Showing
                </Box>
                <Select
                  size="sm"
                  width="70px"
                  value={itemsPerPage}
                  onChange={(e) => {setItemsPerPage(Number(e.target.value));setIsAddingNew(false);}}
                  mr={2}
                >
                  {pageSizeOptions.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </Select>
                <Box as="span" fontSize="sm">
                  of {currentTotalCount ?? 0} entries
                </Box>
              </Box>
            ) : (
              <Box as="span" fontSize="sm" mt={2}>
                Showing total {currentTotalCount ?? 0} entries
              </Box>
            )}
            {totalPages > 1 && (
              <Pagination
                currentPage={page}
                numberOfPages={Math.ceil(currentTotalCount / itemsPerPage)}
                onChange={(page) => {setPage(page);setIsAddingNew(false);}}
              />
            )}
          </Flex>
        </Box>
      </Card>
    </VStack>
  );
};
